import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { buildCartItemFromProduct } from './cartItemBuilder';
import { PLATFORM, BASE, appPath, allApps } from './platform';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
export { API };

/**
 * iter-119b: canonical IST date helpers.
 *
 * The old ad-hoc helpers used
 *   `now.getTime() + (5.5*60 - now.getTimezoneOffset()) * 60000`
 * which double-counts the device offset. On an IST phone
 * (getTimezoneOffset() === -330) this adds 11 hours, flipping the
 * date at 18:30 IST — that's the bug that broke walk-in form dates
 * for salespeople after 6:30 PM. The bug hid in preview because
 * the container runs in UTC (offset === 0).
 *
 * Correct math: `Date.getTime()` is always UTC epoch ms. IST wall
 * time = UTC + 5:30. Adding 330 minutes and reading the ISO date
 * gives the same YYYY-MM-DD regardless of where the browser sits.
 */
export const todayIst = () =>
  new Date(Date.now() + 330 * 60000).toISOString().slice(0, 10);

/** Return today+n (IST) as YYYY-MM-DD; negative n → past. */
export const addDaysIst = (n) =>
  new Date(Date.now() + 330 * 60000 + n * 86400000).toISOString().slice(0, 10);

/** Monday of the current IST week as YYYY-MM-DD (Monday-start weeks). */
export const istWeekStart = () => {
  const dow = (new Date(Date.now() + 330 * 60000).getUTCDay() + 6) % 7; // 0 = Monday
  return addDaysIst(-dow);
};

/** First day of the current IST month as YYYY-MM-DD. */
export const istMonthStart = () => `${todayIst().slice(0, 8)}01`;

// ─── GLOBAL AXIOS ERROR INTERCEPTOR ───
// Fix: when a blob-response endpoint (like PDF) returns an error, axios delivers
// the error body as a Blob. This interceptor reads that blob, parses the JSON,
// and replaces response.data with the parsed object so `err.response.data.detail`
// works uniformly everywhere.
// Also handles 401 (Token expired) by auto-logging out and redirecting to login.
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Convert blob error bodies to JSON
    if (error.response && error.response.data instanceof Blob) {
      try {
        const text = await error.response.data.text();
        try {
          error.response.data = JSON.parse(text);
        } catch {
          error.response.data = { detail: text || 'Request failed' };
        }
      } catch {
        error.response.data = { detail: 'Request failed' };
      }
    }
    // FastAPI sends `detail` as a list of {loc, msg} for a 422. Every screen
    // shows `err.response.data.detail` as text (toasts, inline errors), and a
    // list there is "Objects are not valid as a React child": make it text.
    const d = error.response?.data?.detail;
    if (d != null && typeof d !== 'string') {
      error.response.data.detail = Array.isArray(d)
        ? d.map((x) => (x && (x.msg || x.message)) || String(x)).join('; ')
        : d.message || d.msg || JSON.stringify(d);
    }
    // Auto-logout on 401 (token expired / invalid)
    if (error.response && error.response.status === 401) {
      const isLoginCall = /\/auth\/(login|platform-exchange)/.test(error.config?.url || '');
      if (!isLoginCall) {
        localStorage.removeItem('heda_token');
        localStorage.removeItem('user');
        delete axios.defaults.headers.common['Authorization'];
        if (window.location.pathname !== `${BASE}/login`) {
          // Come back to the same page after logging in again (the /login
          // route honours `redirect`, as the QR flow does).
          window.location.href = `${BASE}/login?session=expired&redirect=` + encodeURIComponent(appPath());
        }
      }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);
const CartContext = createContext(null);

// ─── AUTH PROVIDER ───
export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    // Try to restore user from localStorage on initial load
    const savedUser = localStorage.getItem('user');
    return savedUser ? JSON.parse(savedUser) : null;
  });
  const [token, setToken] = useState(localStorage.getItem('heda_token'));
  const [loading, setLoading] = useState(true);

  // Auth restore on mount / token change. Wrapped in queueMicrotask so the
  // state setters (`setLoading`, `setToken`, `setUser`) fire after the effect
  // returns — satisfies React 19's `react-hooks/set-state-in-effect` rule.
  useEffect(() => {
    let cancelled = false;
    queueMicrotask(() => {
      if (cancelled) return;
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        axios.get(`${API}/auth/me`).then(res => {
          if (cancelled) return;
          setUser(res.data);
          localStorage.setItem('user', JSON.stringify(res.data));
        }).catch(() => {
          if (cancelled) return;
          localStorage.removeItem('heda_token');
          localStorage.removeItem('user');
          setToken(null);
        }).finally(() => { if (!cancelled) setLoading(false); });
      } else {
        setLoading(false);
      }
    });
    return () => { cancelled = true; };
  }, [token]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    const { token: t, user: u } = res.data;
    localStorage.setItem('heda_token', t);
    localStorage.setItem('user', JSON.stringify(u));  // Store user data for role checks
    axios.defaults.headers.common['Authorization'] = `Bearer ${t}`;
    setToken(t);
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem('heda_token');
    localStorage.removeItem('user');  // Clear user data on logout
    delete axios.defaults.headers.common['Authorization'];
    if (PLATFORM) { allApps(); return; }  // back to the platform's launcher
    setToken(null);
    setUser(null);
  };

  // Role permissions come from the server (/auth/me and login) and are
  // edited by the admin on Admin > System > Roles. Admin holds every one.
  const can = (perm) => !!user && (user.role === 'admin' || (user.permissions || []).includes(perm));

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      login, 
      logout, 
      loading, 
      can,
      isAdmin: user?.role === 'admin',
      isManager: user?.role === 'manager',
      isStockManager: user?.role === 'stock_manager',
      canManageProducts: can('products.manage'),
      canViewQr: can('price_lists.manage'),
      // The Admin screen opens for anyone holding one of its tabs.
      canOpenAdmin: ADMIN_PANEL_PERMISSIONS.some(can),
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// Permissions that each give at least one tab on the Admin screen.
const ADMIN_PANEL_PERMISSIONS = [
  'products.manage', 'users.view_team', 'reports.today', 'reports.company', 'walkins.view_all',
  'leads.view_all', 'complaints.view_all', 'catalog.health', 'whatsapp.admin',
  'system.errors', 'settings.edit', 'system.backup',
];

// ─── CART PROVIDER ───
export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    const saved = localStorage.getItem('heda_cart');
    if (!saved) return [];
    try {
      const parsed = JSON.parse(saved);
      if (!Array.isArray(parsed)) return [];
      // iter-104 heal — old cart entries whose `amount` is NaN (the
      // legacy Quick Add Veneer flow used to land `calculated_rate =
      // undefined → amount = NaN`, rendering ₹0 in the cart and Grand
      // Total). Recompute from `net_rate || calculated_rate || rate`
      // and `quantity` so existing users don't have to clear their
      // cart manually after the fix lands.
      return parsed.map((it) => {
        if (!it || typeof it !== 'object') return it;
        const amt = Number(it.amount);
        if (!isNaN(amt) && amt > 0) return it;
        const perUnit = Number(
          it.net_rate ?? it.calculated_rate ?? it.rate ?? 0
        ) || 0;
        const qty = Number(it.quantity) || 0;
        if (perUnit <= 0 || qty <= 0) return it;
        return {
          ...it,
          calculated_rate: Number(it.calculated_rate) || perUnit,
          net_rate:        Number(it.net_rate)        || perUnit,
          // iter-133 / iter-134: honour the per-line Special Discount
          // AND its type when recomputing from a stale localStorage
          // cart. Without this, the reduction would silently disappear
          // on a page refresh that fell through this branch.
          amount:          (() => {
            const gross = perUnit * qty;
            const splT = ((it.special_discount_type || 'amount') + '').toLowerCase();
            const splV = Math.max(0, Number(it.special_discount) || 0);
            const red = splT === 'percent' ? gross * (Math.min(100, splV) / 100) : splV;
            return Math.max(0, gross - red);
          })(),
        };
      });
    } catch {
      return [];
    }
  });
  const [customer, setCustomer] = useState({
    name: '', mobile: '', address: '', gst_no: '', project_name: '', delivery_location: '',
    carpenter_name: '', architect_name: ''
  });

  useEffect(() => {
    localStorage.setItem('heda_cart', JSON.stringify(items));
  }, [items]);

  const addItem = useCallback((product, config) => {
    const { size, thickness } = config;
    // All product-field copying lives in
    // `buildCartItemFromProduct` — the SINGLE source of truth so
    // addItem, replaceItem etc. can never drift again. See
    // frontend/src/lib/cartItemBuilder.js.
    // Forward config.is_quick_add / customDescription into
    // overrides so quick-add flows (Custom/Laminate/Hardware/Adhesive)
    // can flag their items as quick-add for long-term pencil-edit.
    const explicitOverrides = { cart_id: `${product.id}-${size}-${thickness}-${Date.now()}` };
    if (config.is_quick_add) explicitOverrides.is_quick_add = true;
    if (config.customDescription) explicitOverrides.description = config.customDescription;
    const newItem = buildCartItemFromProduct(product, config, explicitOverrides);
    setItems(prev => [...prev, newItem]);
  }, []);

  // Quick add veneer (or other category via main_category override) for the
  // current quotation only — not saved to the master product database.
  // CRITICAL: for sqft-priced categories (Veneers, Plywood, Doors, etc.)
  // the `rate` parameter is treated as RATE PER SQUARE FOOT and is
  // multiplied by the sheet's sqft area BEFORE storing. For per-piece
  // categories (Laminates, Hardware, Adhesives) it's stored as-is.
  const addQuickVeneer = useCallback((veneerData) => {
    const { species, product_code, size, rate, quantity, unit } = veneerData;
    const category = veneerData.main_category || 'Veneers';
    const itemId = `quick-veneer-${Date.now()}`;
    // Quick Add explicit overrides (passed by the CartPage handlers).
    const brandOverride = veneerData.brand || '';
    const showBrand = veneerData.show_brand !== false; // default true
    // `uom` (laminate Quick Add): 'sqft' forces sqft expansion regardless of
    // SQFT_CATEGORIES. 'sheet' / 'piece' stores the rate as-is (no size math).
    const uom = (veneerData.uom || '').toLowerCase();
    const unitUpper = String(unit || '').toUpperCase();
    const isSqftUnit = unitUpper === 'SQ FT' || unitUpper === 'SQFT' || unitUpper === 'SQ.FT';
    const isSqftCategory = SQFT_CATEGORIES.has(category);
    const sqftPerSheet = size ? sizeToSqft(size, category) : 0;

    // Decide whether to expand the rate by sqft:
    //   • Sqft categories with a unit that indicates per-piece pricing
    //     (NOS / SHEET) → multiply rate × sqft per sheet → per-sheet rate
    //   • Sqft categories with unit SQ FT → user is buying loose sqft; the
    //     `quantity` field already holds the total sqft, so rate × qty is
    //     correct without multiplying by sheet sqft.
    //   • Non-sqft categories → store rate as-is (unless uom === 'sqft').
    //
    // CRITICAL (iter 105 — Milled Fluted bug): when the staff picks a real
    // PANEL size (8x4, 10x4, 6x3 etc.) AND the product is per-sqft, we
    // ALWAYS expand to per-sheet billing — even if the unit dropdown was
    // left on SQ FT. The Quick Add Veneer dialog only offers standard
    // panel sizes; "qty in sqft" only makes sense for loose veneer with
    // no real panel size. Without this guard a single mis-clicked
    // dropdown silently undercharged the customer 32×: an 8x4 panel at
    // ₹1,250/sqft billed as ₹3,750 instead of ₹40,000 per sheet.
    // Threshold: ≥ 16 sqft → it's a panel (smallest standard size is
    // 6x3 = 18 sqft); below that → loose sqft / cut piece.
    const isStandardPanel = sqftPerSheet >= 16;
    const expandToPerSheet = isStandardPanel
      || (uom === 'sqft' && !isSqftUnit && sqftPerSheet > 0)
      || (isSqftCategory && !isSqftUnit && sqftPerSheet > 0 && uom !== 'sheet' && uom !== 'piece');

    const ratePerUnit = expandToPerSheet ? rate * sqftPerSheet : rate;
    const qty = quantity || 1;
    // When we force-expanded a panel, normalize the unit to SHEET so the
    // cart's unit column doesn't read "SQ FT" while the math is per
    // sheet — the display must always agree with the math.
    const effectiveUnit = (isStandardPanel && isSqftUnit) ? 'SHEET' : unit;

    const newItem = {
      cart_id: itemId,
      product_id: `quick-${itemId}`,
      description: `${species} ${product_code ? `(${product_code})` : ''}`.trim(),
      brand: brandOverride,
      main_category: category,
      size: size || '',
      quantity: qty,
      unit: effectiveUnit || (uom === 'sqft' || isSqftCategory ? 'NOS' : 'SQ FT'),
      // When a panel is force-expanded the displayed `rate` must
      // also be the per-sheet figure so the cart's rate column shows
      // "₹40,000/sheet × 3 = ₹1,20,000" — matching the math — rather than
      // "₹1,250/sq ft × 3 SQ FT = ₹3,750" which would silently undercharge.
      rate: (isStandardPanel && isSqftUnit) ? ratePerUnit : rate,
      // When unit=SQ FT, the rate is logically per-sqft regardless of
      // whether we expanded by sheet area (we don't, for unit=SQ FT) —
      // label it so the cart's rate column shows "₹235/sq ft" instead of
      // a misleading "₹235/sheet".
      // Panel-expansion forces rate_type back to per-sheet
      // billing so the cart label matches the math (₹40,000/sheet instead
      // of ₹1,250/sq ft × 3 SQ FT = ₹3,750 nonsense).
      rate_type: (isStandardPanel && isSqftUnit)
        ? 'per sheet'
        : ((expandToPerSheet || isSqftUnit) ? 'per sq ft' : (uom === 'piece' ? 'per piece' : 'per sheet')),
      calculated_rate: ratePerUnit,
      discount_type: 'percent',  // Veneers use flat discount (same as Laminates)
      discount_value: 0,
      cascading_discount: '',
      cascading_discount_type: 'percent',
      net_rate: ratePerUnit,
      amount: ratePerUnit * qty,
      // Quick add specific fields
      product_code: product_code || '',
      species: species || '',
      finish: veneerData.finish || '',
      // iter 114: Quick Add Laminate/Highlighter now persists an explicit
      // user-typed Description (renamed from "Finish") and an optional
      // Thickness. The PDF renderer reads these so the line is built
      // canonically — `description` (the cart-label string) is just a
      // human-readable mirror and not the source of truth.
      description_text: veneerData.description_text || '',
      thickness: veneerData.thickness || '',
      // Allow Quick Add flows to pass through a
      // `brand_description` so Plywood/Blockboard/MDF/Doors/WPC
      // brand-header rows survive Quick-Add-based creation too. Empty
      // string when the dialog doesn't collect it — same behaviour as
      // before.
      brand_description: veneerData.brand_description || '',
      is_quick_add: true,
      show_brand: showBrand,
      color: '',
      feature: '',
      hardware_system_type: '',
    };
    setItems(prev => [...prev, newItem]);
    return newItem;
  }, []);

  const removeItem = useCallback((cartId) => {
    setItems(prev => prev.filter(i => i.cart_id !== cartId));
  }, []);

  const updateItem = useCallback((cartId, updates) => {
    setItems(prev => prev.map(item => {
      if (item.cart_id !== cartId) return item;
      const updated = { ...item, ...updates };
      // Recalculate net rate based on discount
      let netRate = updated.calculated_rate;

      // Iter 115: every category supports cascading discount. The
      // per-item `discount_type` flag is the only gate now.
      const itemIsCascading = isCascadingCategory(updated.main_category);

      if (itemIsCascading && updated.discount_type === 'cascading' && updated.cascading_discount) {
        // Parse cascading discount string like "10+3+3" (percent) OR "25+10" (rupees)
        const parts = String(updated.cascading_discount).split('+').map(p => parseFloat(p.trim())).filter(p => !isNaN(p) && p > 0);
        if (parts.length > 0) {
          const cascMode = (updated.cascading_discount_type || 'percent').toLowerCase();
          if (cascMode === 'rupees') {
            // RUPEE mode (iter 100): each token = per-UNIT rupee deduction
            // (typically per-sqft). For sqft-category items, calculated_rate
            // is rate × sqftPerSheet so we must scale the rupee subtraction
            // by sqftPerSheet too — otherwise "25 rupees off" on a 8x4 sheet
            // would only deduct ₹25 instead of ₹25 × 32 = ₹800.
            const rateVal = Number(updated.rate || 0);
            const calcVal = Number(updated.calculated_rate || 0);
            const scale = rateVal > 0 ? (calcVal / rateVal) : 1;
            let currentRate = calcVal;
            for (const rupees of parts) {
              currentRate -= rupees * scale;
            }
            netRate = currentRate;
          } else {
            // PERCENT mode (default)
            let currentRate = updated.calculated_rate;
            for (const percent of parts) {
              currentRate = currentRate * (1 - percent / 100);
            }
            netRate = currentRate;
          }
        }
      } else if (updated.discount_value > 0) {
        // Standard discount (percent or amount)
        if (updated.discount_type === 'percent') {
          netRate = updated.calculated_rate * (1 - updated.discount_value / 100);
        } else {
          netRate = updated.calculated_rate - updated.discount_value;
        }
      }
      
      updated.net_rate = Math.max(0, netRate);
      // iter-133 / iter-134: per-item Special Discount applied to the
      // line amount. Type is 'amount' (₹ absolute) or 'percent' (% of
      // gross line). This is the ONE authoritative place item.amount
      // is written on the client; the backend just sums item.amount
      // → grand_total already reflects the reduction.
      const spl = Math.max(0, Number(updated.special_discount) || 0);
      updated.special_discount = spl;
      const splType = (updated.special_discount_type || 'amount').toLowerCase();
      updated.special_discount_type = splType === 'percent' ? 'percent' : 'amount';
      const gross134 = updated.net_rate * updated.quantity;
      const reduction134 = splType === 'percent'
        ? gross134 * (Math.min(100, spl) / 100)
        : spl;
      updated.amount = Math.max(0, gross134 - reduction134);
      return updated;
    }));
  }, []);

  const clearCart = useCallback(() => {
    setItems([]);
    setCustomer({ name: '', mobile: '', address: '', gst_no: '', project_name: '', delivery_location: '' });
  }, []);

  // Replace an existing item with a new product (for comparison quotations)
  const replaceItem = useCallback((cartId, product, config) => {
    setItems(prev => prev.map(item => {
      if (item.cart_id !== cartId) return item;

      // `buildCartItemFromProduct` is the SINGLE source of
      // truth for turning a catalog product into a cart line. Passing
      // the original item's cart_id + section + discount settings as
      // overrides means the Replace preserves the customer-relevant
      // in-cart state (position, section assignment, discount) while
      // pulling every PDF-relevant field (brand_description, subcategory,
      // grade, etc.) from the NEW product. This closed the recurring
      // "brand description gone after edit" regression — see
      // frontend/src/lib/__tests__/replaceItem_brandDescription.test.js.
      const effectiveQuantity = config.quantity || item.quantity;
      return buildCartItemFromProduct(
        product,
        { ...config, quantity: effectiveQuantity },
        {
          cart_id: item.cart_id,
          // Keep the customer's in-cart edits — discount + section.
          discount_type: item.discount_type || 'percent',
          discount_value: item.discount_value || 0,
          cascading_discount: item.cascading_discount || '',
          cascading_discount_type: item.cascading_discount_type || 'percent',
          section: item.section || '',
          // main_category falls back to the original if the new product
          // doesn't have one (rare — catalog products always do).
          main_category: product.main_category || item.main_category,
        }
      );
    }));

    // Clear the replace mode from sessionStorage
    sessionStorage.removeItem('replaceCartId');
    sessionStorage.removeItem('replaceCategory');
  }, []);

  const loadQuotationForEdit = useCallback((quotation) => {
    // Load quotation items into cart
    const cartItems = quotation.items.map((item, idx) => {
      // CRITICAL: for sqft-priced products (Plywood, Veneers, Doors, MDF,
      // WPC/PVC, Blockboard), `item.rate` is stored as per-sqft (e.g. 175)
      // but the cart uses `calculated_rate` as the PER-SHEET rate (e.g.,
      // 175 × 24 = 4200 for a 6x4 sheet). If we just copy `rate` into
      // `calculated_rate`, the cart treats per-sqft as per-sheet on the
      // first edit and the amounts collapse. Mirror the logic from
      // `addItem`/`addQuickVeneer` so edits stay consistent.
      const cat = item.main_category || 'Others';
      const rateType = (item.rate_type || '').toLowerCase();
      const unitUpper = String(item.unit || '').toUpperCase();
      const isSqftUnit = unitUpper === 'SQ FT' || unitUpper === 'SQFT' || unitUpper === 'SQ.FT';
      const isSqftRate = rateType === 'per sq ft' || rateType === 'per sqft';
      const isSqftCategory = SQFT_CATEGORIES.has(cat);
      const sqftPerSheet = (isSqftCategory && item.size) ? sizeToSqft(item.size, cat) : 0;
      // CRITICAL bug fix (iter 104): the per-sqft expansion was previously
      // gated on `!isSqftUnit` — but Quick Add Veneer/Laminate items store
      // `unit='SQ FT'` by default even though their `quantity` represents
      // SHEETS (the user typed "2" meaning "2 sheets"). On reload that gate
      // skipped expansion and the amount silently collapsed to rate × qty
      // (e.g. ₹140 × 2 = ₹280 instead of ₹140 × 32 × 2 = ₹8,960 for an 8×4
      // sheet).
      //
      // We now use `rate_type` as the source of truth: ANY item saved with
      // `rate_type === 'per sq ft'` (Quick Add or catalog) gets its rate
      // expanded by the sheet area on reload. As a safety net we also
      // cross-check the saved `amount`: if `amount / quantity` already
      // matches the expanded rate (within a ₹1 tolerance) we trust the
      // saved amount and back-derive the calculated_rate from it — this
      // protects against per-sqft items where qty truly represented loose
      // square feet (rare but possible legacy data).
      const expandByRateType = isSqftRate && sqftPerSheet > 0;
      const expandByCategory = isSqftCategory && !isSqftUnit && sqftPerSheet > 0;
      const expandToPerSheet = expandByRateType || expandByCategory;
      let calcRate = expandToPerSheet
        ? Number(item.rate || 0) * sqftPerSheet
        : Number(item.rate || 0);

      // Safety net: if the saved amount disagrees with our recomputed
      // (rate × sqft × qty), prefer the saved amount and back-derive
      // calculated_rate from it. Covers legacy rows where qty was loose
      // sqft instead of sheets.
      const qtyForCheck = Number(item.quantity || 0);
      const savedAmount = Number(item.amount || 0);
      const discPct = item.discount_type === 'percent' ? Number(item.discount_value || 0) : 0;
      const discAbs = item.discount_type === 'amount'  ? Number(item.discount_value || 0) : 0;
      if (expandToPerSheet && qtyForCheck > 0 && savedAmount > 0) {
        const expectedNet = calcRate * (1 - discPct / 100) - discAbs;
        const expectedAmt = expectedNet * qtyForCheck;
        const actualNet = savedAmount / qtyForCheck;
        // If saved amount diverges by >₹1 AND the saved per-unit net is
        // close to the un-expanded `item.rate`, we infer that qty was
        // already in sqft → don't re-expand, trust saved amount.
        if (Math.abs(expectedAmt - savedAmount) > 1
            && Math.abs(actualNet - Number(item.rate || 0)) < 1) {
          calcRate = Number(item.rate || 0);
        }
      }
      // Once calcRate is locked in, fully reconstruct net_rate + amount
      // from it so the cart never shows a stale (wrongly-collapsed) value
      // even before the user makes their first edit. This honours the
      // standard discount and the cascading-discount (% or ₹) modes —
      // exactly mirroring `updateItem`'s logic. For non-sqft items where
      // calcRate hasn't changed, this is a no-op (recomputes the same
      // amount).
      let computedNet = calcRate;
      if (isCascadingCategory(cat) && item.discount_type === 'cascading' && item.cascading_discount) {
        const parts = String(item.cascading_discount).split('+')
          .map(p => parseFloat(p.trim()))
          .filter(p => !isNaN(p) && p > 0);
        const cascMode = (item.cascading_discount_type || 'percent').toLowerCase();
        if (cascMode === 'rupees') {
          const baseR = Number(item.rate || 0);
          const scale = baseR > 0 ? (calcRate / baseR) : 1;
          let cur = calcRate;
          parts.forEach(r => { cur -= r * scale; });
          computedNet = cur;
        } else {
          let cur = calcRate;
          parts.forEach(p => { cur = cur * (1 - p / 100); });
          computedNet = cur;
        }
      } else if (Number(item.discount_value || 0) > 0) {
        if (item.discount_type === 'percent') {
          computedNet = calcRate * (1 - Number(item.discount_value) / 100);
        } else {
          computedNet = calcRate - Number(item.discount_value);
        }
      }
      computedNet = Math.max(0, computedNet);
      // iter-133 / iter-134: subtract per-line Special Discount at
      // load so an edit cycle preserves the reduction with the
      // correct type.
      const splType134 = ((item.special_discount_type || 'amount') + '').toLowerCase();
      const splVal134 = Math.max(0, Number(item.special_discount) || 0);
      const gross134 = computedNet * Number(item.quantity || 0);
      const reduction134 = splType134 === 'percent'
        ? gross134 * (Math.min(100, splVal134) / 100)
        : splVal134;
      const computedAmount = Math.max(0, gross134 - reduction134);

      return {
        cart_id: `edit-${quotation.id}-${idx}-${Date.now()}`,
        product_id: item.product_id,
        description: item.description,
        brand: item.brand,
        main_category: cat,
        size: item.size,
        thickness: item.thickness,
        quantity: item.quantity,
        unit: item.unit,
        rate: item.rate,
        rate_type: item.rate_type || '',
        calculated_rate: calcRate,
        discount_type: item.discount_type || 'percent',
        discount_value: item.discount_value || 0,
        cascading_discount: item.cascading_discount || '',  // For Plywood/Veneers
        cascading_discount_type: item.cascading_discount_type || 'percent',
        net_rate: computedNet,
        amount: computedAmount,
        // iter-133 / iter-134: per-line Special Discount + its type
        // carried across edit cycles so the reduction round-trips
        // correctly (₹ vs %).
        special_discount: Math.max(0, Number(item.special_discount) || 0),
        special_discount_type: ((item.special_discount_type || 'amount') + '').toLowerCase() === 'percent' ? 'percent' : 'amount',
        color: item.color,
        feature: item.feature,
        hardware_system_type: item.hardware_system_type,
        // Additional fields for brand hiding
        product_code: item.product_code || '',
        species: item.species || '',
        finish: item.finish || '',
        // iter 114: explicit Quick Add Description (user-typed) +
        // Thickness must survive an edit cycle so the PDF renderer keeps
        // building the canonical "DESCRIPTION - CODE - THICKNESS" line.
        description_text: item.description_text || item.finish || '',
        is_quick_add: item.is_quick_add || false,
        nail_number: item.nail_number || '',
        // Default to True for legacy items missing this field so existing
        // Hardware items keep showing their brand after edit.
        show_brand: item.show_brand !== false,
        // Section/Room assignment
        section: item.section || '',
        // BUG FIX (iter 99): preserve PDF header fields across edits.
        // Without these, editing a Plywood/Blockboard/MDF/Doors/WPC quotation
        // and re-downloading the PDF lost the brand header row (the
        // "EVEREST EXCEL BWP 710 (21 YRS WARRANTY)" line).
        brand_description: item.brand_description || '',
        subcategory: item.subcategory || '',
        grade: item.grade || '',
        veneer_type: item.veneer_type || '',
        pack_size: item.pack_size || '',
        variant_size: item.variant_size || '',
        hardware_type: item.hardware_type || '',
        product_name: item.product_name || '',
      };
    });
    setItems(cartItems);
    
    // Load customer details
    if (quotation.customer) {
      setCustomer({
        name: quotation.customer.name || '',
        mobile: quotation.customer.mobile || '',
        address: quotation.customer.address || '',
        gst_no: quotation.customer.gst_no || '',
        project_name: quotation.customer.project_name || '',
        delivery_location: quotation.customer.delivery_location || '',
        carpenter_name: quotation.customer.carpenter_name || '',
        architect_name: quotation.customer.architect_name || '',
      });
    }
    
    // Return sections for the CartPage to load
    return quotation.sections || [];
  }, []);

  const subtotal = items.reduce((sum, i) => sum + i.amount, 0);

  return (
    <CartContext.Provider value={{ items, addItem, addQuickVeneer, removeItem, updateItem, replaceItem, clearCart, loadQuotationForEdit, customer, setCustomer, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);

// ─── API HELPERS ───
export const api = {
  getProducts: (params) => axios.get(`${API}/products`, { params }),
  getProduct: (id) => axios.get(`${API}/products/${id}`),
  createProduct: (data) => axios.post(`${API}/products`, data),
  quickAddProduct: (data) => axios.post(`${API}/products/quick-add`, data),
  updateProduct: (id, data) => axios.put(`${API}/products/${id}`, data),
  deleteProduct: (id) => axios.delete(`${API}/products/${id}`),
  deleteOrphanProducts: (passcode) => axios.delete(`${API}/products/orphans/all`, { headers: { 'X-Action-Passcode': passcode || '' } }),
  toggleProductStatus: (id) => axios.patch(`${API}/products/${id}/status`),
  uploadProductImage: (id, formData) => axios.post(`${API}/products/${id}/upload-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  bulkUpload: (formData) => axios.post(`${API}/products/bulk-upload`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  exportTemplate: (category) => axios.get(`${API}/products/export-template`, { params: category ? { category } : {}, responseType: 'blob' }),
  exportAllProducts: () => axios.get(`${API}/products/export-all`, { responseType: 'blob' }),
  getBrands: () => axios.get(`${API}/products/brands`),
  getCategories: () => axios.get(`${API}/products/categories`),
  getSubcategories: (category) => axios.get(`${API}/products/subcategories`, { params: { category } }),
  // Lookup matching products for the Comparison "Replace" dialog.
  // Filters by main_category + (optional) match_thickness + (optional) match_size.
  getProductsMatching: ({ category, match_thickness, match_size, limit = 100 }) =>
    axios.get(`${API}/products`, { params: { category, match_thickness: match_thickness || '', match_size: match_size || '', limit } }),
  createQuotation: (data) => axios.post(`${API}/quotations`, data),
  updateQuotation: (id, data) => axios.put(`${API}/quotations/${id}`, data),
  deleteQuotation: (id) => axios.delete(`${API}/quotations/${id}`),
  getQuotations: (params) => axios.get(`${API}/quotations`, { params }),
  getStalePendingSummary: (params = {}) => axios.get(`${API}/quotations/stale-pending-summary`, { params }),
  getMyQuotationFollowups: () => axios.get(`${API}/quotations/my-followups`),
  setQuotationFollowUp: (id, follow_up_date) => axios.patch(`${API}/quotations/${id}/follow-up`, { follow_up_date }),
  getQuotationsByUser: (userId, params) => axios.get(`${API}/quotations/by-user/${userId}`, { params }),
  getQuotation: (id) => axios.get(`${API}/quotations/${id}`),
  duplicateQuotation: (id) => axios.post(`${API}/quotations/${id}/duplicate`),
  downloadPdf: (id, internal = false) => axios.get(`${API}/quotations/${id}/pdf`, { params: { internal }, responseType: 'blob' }),
  downloadInternalPdf: (id) => axios.get(`${API}/quotations/${id}/pdf`, { params: { internal: true }, responseType: 'blob' }),
  downloadQuotationPDF: (id, internal = false) => axios.get(`${API}/quotations/${id}/pdf`, { params: { internal }, responseType: 'blob' }),
  // Renders a PDF for an UNSAVED payload (iter-104d Preview button).
  // Same payload shape as `createQuotation` — backend wraps it in a
  // synthetic doc, runs the regular PDF pipeline and returns the file
  // with `Content-Disposition: inline` so we open it in a new tab.
  previewQuotationPdf: (payload, internal = false) =>
    axios.post(`${API}/quotations/preview-pdf`, payload, { params: { internal }, responseType: 'blob' }),
  // Comparison quotations
  createComparisonQuotation: (id) => axios.post(`${API}/quotations/${id}/create-comparison`),
  getComparisonGroup: (id) => axios.get(`${API}/quotations/${id}/comparison-group`),
  downloadComparisonPdf: (id, internal = false) => axios.get(`${API}/quotations/${id}/comparison-pdf`, { params: { internal }, responseType: 'blob' }),
  deleteComparison: (id, keepOption) => axios.post(`${API}/quotations/${id}/delete-comparison`, null, { params: { keep_option: keepOption } }),
  getDashboardStats: () => axios.get(`${API}/dashboard/stats`),
  getSalesStats: (params) => axios.get(`${API}/admin/sales-stats`, { params }),
  getSalespersonDrilldown: (params) => axios.get(`${API}/admin/salesperson-drilldown`, { params }),
  getAdminAnalytics: (params = {}) => axios.get(`${API}/admin/analytics`, { params }),
  getReferrerAnalytics: ({ search = '', kind = '' } = {}) => axios.get(`${API}/admin/referrer-analytics`, { params: { search, kind } }),
  getPOAnalytics: () => axios.get(`${API}/admin/po-analytics`),
  getMyAnalytics: (params = {}) => axios.get(`${API}/admin/my-analytics`, { params }),
  getCatalogHealth: () => axios.get(`${API}/admin/catalog-health`),
  bulkDeleteCatalogHealth: (ids) => axios.delete(`${API}/admin/catalog-health/bulk`, { data: { ids } }),
  // AI-clustered Lost Reasons summary (AI groups variants like
  // "price too high" / "price very high" / "found expensive" into themes).
  getLostReasonsSummary: () => axios.get(`${API}/admin/lost-reasons-summary`),
  // Daily owner-summary WhatsApp digest — admin-only.
  previewDailySummary: () => axios.get(`${API}/admin/daily-summary/preview`),
  sendDailySummary: (phone = '') => axios.post(`${API}/admin/daily-summary/send`, { phone }),
  // Diagnostic snapshot — last attempt + next scheduled fire + configured phones.
  getDailySummaryHealth: () => axios.get(`${API}/admin/daily-summary/health`),
  // Diagnostic: send a free-form text (no template). Used to triangulate
  // whether the digest 403 is an API-key issue or a template-only issue.
  sendDailySummaryTestText: (phone) => axios.post(`${API}/admin/daily-summary/test-text`, { phone }),
  // DoubleTick key/API health probe — surfaces "key rejected" vs "template
  // issue" vs "network error" so admins can act without reading logs.
  verifyDoubleTick: () => axios.get(`${API}/admin/doubletick/verify`),
  // Iter-124 analytics comparison — MTD vs same-days-prior-month by default;
  // caller can pass any 4 dates to compare arbitrary windows.
  compareAnalyticsPeriods: (params = {}) => axios.get(`${API}/admin/analytics/compare`, { params }),
  // Iter-128 — download the salesperson-review PDF as a Blob so we can
  // trigger a browser save and prompt the owner to WhatsApp-share it.
  exportPresentationPdf: (body) => axios.post(
    `${API}/admin/analytics/presentation-pdf`,
    body,
    { responseType: 'arraybuffer' },
  ),
  // Iter-126 — pending pipeline ageing buckets (fresh / warm / cold / stale)
  getPipelineHealth: (params = {}) => axios.get(`${API}/admin/analytics/pipeline-health`, { params }),
  // Full database backup — admin-only, streams a zip with one JSON per collection.
  downloadBackup: () => axios.get(`${API}/admin/backup`, { responseType: 'blob' }),
  // Iter-124 test-reminder buttons — send a sample walk-in / lead
  // follow-up reminder to a phone (defaults to admin's configured phone).
  testWalkinReminder: (phone) => axios.post(`${API}/admin/test-reminder/walkin`, { phone }),
  testLeadReminder: (phone) => axios.post(`${API}/admin/test-reminder/lead`, { phone }),
  // Iter-123 DoubleTick permanent-fix: DB-backed API-key + WABA number.
  // Admins can rotate the key from Settings without redeploy/restart.
  getDoubletickCredentials: () => axios.get(`${API}/admin/doubletick/credentials`),
  updateDoubletickCredentials: (body) => axios.put(`${API}/admin/doubletick/credentials`, body),
  // Update a quotation's order status (closed/pending/lost). Pass
  // `lost_reason` when status='lost' — the backend rejects empty reasons.
  updateQuotationStatus: (id, { status, lost_reason = '' }) =>
    axios.put(`${API}/quotations/${id}/status`, { status, lost_reason }),
  getRolePermissions: () => axios.get(`${API}/admin/roles`),
  saveRolePermissions: (matrix, passcode) => axios.put(`${API}/admin/roles`, { matrix }, { headers: { 'X-Action-Passcode': passcode || '' } }),
  resetRolePermissions: (passcode) => axios.post(`${API}/admin/roles/reset`, null, { headers: { 'X-Action-Passcode': passcode || '' } }),
  register: (data, passcode) => axios.post(`${API}/auth/register`, data, { headers: { 'X-Action-Passcode': passcode || '' } }),
  deleteUser: (id, passcode) => axios.delete(`${API}/admin/users/${id}`, { headers: { 'X-Action-Passcode': passcode || '' } }),
  getUserSessions: (id) => axios.get(`${API}/admin/users/${id}/sessions`),
  revokeSession: (sessionId) => axios.post(`${API}/admin/sessions/${sessionId}/revoke`),
  revokeAllUserSessions: (id) => axios.post(`${API}/admin/users/${id}/revoke-all-sessions`),
  updateUser: (id, data, passcode) => axios.put(`${API}/admin/users/${id}`, data, { headers: { 'X-Action-Passcode': passcode || '' } }),
  changeActionPasscode: (current_passcode, new_passcode, admin_password = '') =>
    axios.put(`${API}/admin/action-passcode`, { current_passcode, new_passcode, admin_password }),
  getSalespeople: () => axios.get(`${API}/quotations/salespeople`),
  getCustomers: (search) => axios.get(`${API}/customers`, { params: { search } }),
  getCustomerProfile: (customerId) => axios.get(`${API}/customers/${customerId}`),
  getCustomerByMobile: (mobile) => axios.get(`${API}/customers/by-mobile/${mobile}`),
  saveCustomer: (data) => axios.post(`${API}/customers`, data),
  // AI Import
  extractPdf: (formData, category) => axios.post(`${API}/ai-import/extract-pdf?category=${encodeURIComponent(category)}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  extractPdfVision: (formData) => axios.post(`${API}/ai-import/extract-pdf-vision`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 }),
  bulkImportProducts: (products) => axios.post(`${API}/ai-import/bulk-import`, products),
  // QR Code Price Lists
  listQrCodes: () => axios.get(`${API}/qr-codes`),
  getQrCode: (id) => axios.get(`${API}/qr-codes/${id}`),
  getQrImageBlob: (id) => axios.get(`${API}/qr-codes/${id}/image`, { responseType: 'blob' }),
  getQrPdfInfo: (id) => axios.get(`${API}/qr-codes/${id}/pdf-info`),
  getQrPdfPageBlob: (id, pageNum) =>
    axios.get(`${API}/qr-codes/${id}/page/${pageNum}`, { responseType: 'blob' }),
  searchQrPdf: (id, q) => axios.get(`${API}/qr-codes/${id}/search`, { params: { q } }),
  getQrThumbnailBlob: (id) => axios.get(`${API}/qr-codes/${id}/thumbnail`, { responseType: 'blob' }),
  getQrCodeImageBlob: (id, boxSize = 10) => axios.get(`${API}/qr-codes/${id}/qr-image`, { params: { box_size: boxSize }, responseType: 'blob' }),
  createQrCode: (formData) => axios.post(`${API}/qr-codes`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  updateQrLabel: (id, label) => { const fd = new FormData(); fd.append('label', label); return axios.put(`${API}/qr-codes/${id}`, fd); },
  replaceQrImage: (id, formData) => axios.post(`${API}/qr-codes/${id}/replace-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteQrCode: (id) => axios.delete(`${API}/qr-codes/${id}`),
  generateQrPrintPdf: (id, params) => {
    const fd = new FormData();
    Object.entries(params || {}).forEach(([k, v]) => fd.append(k, v));
    return axios.post(`${API}/qr-codes/${id}/print-pdf`, fd, { responseType: 'blob' });
  },
  generateBulkQrPrintPdf: (payload) => axios.post(`${API}/qr-codes/bulk-print-pdf`, payload, { responseType: 'blob' }),
  // Admin storage stats
  getStorageStats: () => axios.get(`${API}/admin/storage-stats`),

  // Voice → Cart (BETA)
  voiceTranscribe: (formData) => axios.post(`${API}/voice/transcribe`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000 }),
  voiceExtract: (transcript, language) => axios.post(`${API}/voice/extract`, { transcript, language }, { timeout: 90000 }),

  // WhatsApp Quotation Agent (Phase B)
  getWhatsappStatus: () => axios.get(`${API}/whatsapp/status`),
  toggleWhatsappUser: (userId, enabled) => axios.patch(`${API}/whatsapp/users/${userId}/whatsapp`, { enabled }),
  toggleWhatsappFeature: (userId, feature, enabled) =>
    axios.patch(`${API}/whatsapp/users/${userId}/whatsapp-feature`, { feature, enabled }),
  // Delivery log + recipients + one-person test send (Admin → WhatsApp)
  getWhatsappDeliveryLog: (params) => axios.get(`${API}/admin/whatsapp/delivery-log`, { params }),
  getWhatsappRecipients: () => axios.get(`${API}/admin/whatsapp/recipients`),
  whatsappTestSend: (userId, template) =>
    axios.post(`${API}/admin/whatsapp/test-send`, { user_id: userId, ...(template ? { template } : {}) }, { timeout: 60000 }),

  // Walk-in Register (WhatsApp-driven log of in-store customer visits)
  listWalkins: (params) => axios.get(`${API}/walkins`, { params }),
  listMyWalkins: (params) => axios.get(`${API}/walkins/mine`, { params }),
  suggestWalkins: (q) => axios.get(`${API}/walkins/suggest`, { params: { q } }),
  createWalkin: (payload) => axios.post(`${API}/walkins`, payload),
  updateWalkin: (id, payload) => axios.put(`${API}/walkins/${id}`, payload),
  rescheduleWalkin: (id, payload) => axios.post(`${API}/walkins/${id}/reschedule`, payload),
  completeWalkin: (id) => axios.post(`${API}/walkins/${id}/complete`),
  setWalkinStatus: (id, payload) => axios.patch(`${API}/walkins/${id}/status`, payload),
  deleteWalkin: (id) => axios.delete(`${API}/walkins/${id}`),
  exportWalkinsXlsx: (params) => axios.get(`${API}/walkins/export.xlsx`, { params, responseType: 'blob' }),

  // Lead Register (pipeline for inquiries that haven't visited the shop yet)
  listLeads: (params) => axios.get(`${API}/leads`, { params }),
  listMyLeads: (params) => axios.get(`${API}/leads/mine`, { params }),
  createLead: (payload) => axios.post(`${API}/leads`, payload),
  updateLead: (id, payload) => axios.put(`${API}/leads/${id}`, payload),
  rescheduleLead: (id, payload) => axios.post(`${API}/leads/${id}/reschedule`, payload),
  completeLead: (id) => axios.post(`${API}/leads/${id}/complete`),
  setLeadStatus: (id, payload) => axios.patch(`${API}/leads/${id}/status`, payload),
  deleteLead: (id) => axios.delete(`${API}/leads/${id}`),
  // Lead Owner picker (active staff / creators / managers), reschedule +
  // reassignment history, and the team follow-ups list.
  getLeadOwners: () => axios.get(`${API}/leads/owners`),
  getLeadHistory: (id) => axios.get(`${API}/leads/${id}/history`),
  getTeamFollowups: (params) => axios.get(`${API}/leads/team-followups`, { params }),

  // Complaints Register (internal complaint log — dashboard entry point,
  // admin/manager see all, other roles see only their own)
  getComplaints: (params) => axios.get(`${API}/complaints`, { params }),
  getComplaint: (id) => axios.get(`${API}/complaints/${id}`),
  createComplaint: (payload) => axios.post(`${API}/complaints`, payload),
  updateComplaint: (id, payload) => axios.put(`${API}/complaints/${id}`, payload),
  deleteComplaint: (id) => axios.delete(`${API}/complaints/${id}`),
  uploadComplaintImage: (id, formData) => axios.post(`${API}/complaints/${id}/images`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  deleteComplaintImage: (id, filename) => axios.delete(`${API}/complaints/${id}/images/${filename}`),
  exportLeadsXlsx: (params) => axios.get(`${API}/leads/export.xlsx`, { params, responseType: 'blob' }),

  // App settings (admin-editable PDF header / terms / bank details)
  getSettings: () => axios.get(`${API}/settings`),
  updateSettings: (payload) => axios.put(`${API}/settings`, payload),
  uploadLogo: (file) => {
    const form = new FormData();
    form.append('file', file);
    return axios.post(`${API}/settings/logo`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteLogo: () => axios.delete(`${API}/settings/logo`),
  // Daily WhatsApp digest (9 PM IST cron) — admin can send a test message now
  // or preview the message body before configuring the recipient phone.
  sendDailySummaryNow: (phone) => axios.post(`${API}/admin/daily-summary/send`, { phone: phone || '' }),
  // Audit & heal saved quotations whose per-sqft amount silently collapsed
  // (iter-104 bug). Pass dry_run=true for preview, false to persist.
  auditHealQuotations: (dryRun = true) => axios.post(`${API}/admin/quotations/audit-heal?dry_run=${dryRun}`),
  // Manually trigger the nightly read-only watchdog (which otherwise runs
  // automatically at 02:00 IST and WhatsApps admins on any findings).
  runNightlyAuditNow: () => axios.post(`${API}/admin/quotations/audit-heal/run-nightly-now`),
  // Catalog hygiene — find products whose name/subcategory suggests a
  // different main_category than they're filed under.
  findMislabeledProducts: () => axios.get(`${API}/admin/products/mislabeled`),
  fixMislabeledProduct: (product_id, new_main_category) =>
    axios.post(`${API}/admin/products/mislabeled/fix`, { product_id, new_main_category }),
  // Returns the URL the admin UI can use directly in <img src=...>; this
  // hits the auth-protected backend route via axios baseURL header.
  getLogoUrl: () => `${API}/settings/logo?_=${Date.now()}`,
  // Stock Management
  getStockList: (params) => axios.get(`${API}/stock`, { params }),
  getStockForProduct: (params) => axios.get(`${API}/stock/by-product`, { params }),
  getStockByBrand: (params) => axios.get(`${API}/stock/by-brand`, { params }),
  createStock: (data) => axios.post(`${API}/stock`, data),
  updateStock: (id, data) => axios.put(`${API}/stock/${id}`, data),
  quickUpdateStock: (params) => axios.put(`${API}/stock/quick-update`, null, { params }),
  deleteStock: (id) => axios.delete(`${API}/stock/${id}`),
  cleanupOrphanStock: () => axios.delete(`${API}/stock/orphans`),
  getStockCategories: () => axios.get(`${API}/stock/categories`),
  getStockBrands: (category) => axios.get(`${API}/stock/brands`, { params: { category } }),
  getStockThicknesses: (category, brand_name) => axios.get(`${API}/stock/thicknesses`, { params: { category, brand_name } }),
  // Stock History
  getStockHistory: (params) => axios.get(`${API}/stock/history`, { params }),
  getProductStockHistory: (params) => axios.get(`${API}/stock/history/by-product`, { params }),
  getStockHistorySummary: (days) => axios.get(`${API}/stock/history/summary`, { params: { days } }),
  getStockCoverage: () => axios.get(`${API}/stock/coverage`),
  syncStockFromProducts: () => axios.post(`${API}/stock/sync-from-products`),
  // Tally Excel import — fuzzy match & approve flow.
  tallyStockPreview: (formData) => axios.post(`${API}/stock/tally-preview`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  tallyStockApply: (data) => axios.post(`${API}/stock/tally-apply`, data),
  tallyCatalogOptions: () => axios.get(`${API}/stock/tally-catalog-options`),
  // Deep health watchdog — status + on-demand run.
  getHealthStatus: () => axios.get(`${API}/admin/health/status`),
  runHealthCheckNow: () => axios.post(`${API}/admin/health/run-now`),
  // Quotation dedupe — duplicate-map for History badges + admin unmerge.
  getQuotationDuplicateMap: () => axios.get(`${API}/quotations/duplicate-map`),
  unmergeQuotation: (id) => axios.post(`${API}/quotations/${id}/unmerge`),
  remergeQuotation: (id) => axios.post(`${API}/quotations/${id}/remerge`),
  // At-save-time "Possible duplicate?" prompt — frontend posts the
  // pending payload here BEFORE saving to surface near-identical recent
  // quotations from the same customer. On "Replace old", caller follows
  // up with supersedeQuotation; on "Keep both", with markQuotationDistinct.
  checkDuplicateQuotations: (payload, editId = '') =>
    axios.post(`${API}/quotations/check-duplicates`, payload, { params: editId ? { edit_id: editId } : {} }),
  supersedeQuotation: (oldId, newId) =>
    axios.post(`${API}/quotations/${oldId}/supersede`, { superseded_by: newId }),
  unsupersedeQuotation: (id) => axios.post(`${API}/quotations/${id}/unsupersede`),
  markQuotationDistinct: (id) => axios.post(`${API}/quotations/${id}/mark-distinct`),
  // Admin tool: list every historical pair the dedupe engine considers
  // a duplicate so the admin can manually resolve them.
  listPossibleDuplicates: () => axios.get(`${API}/admin/quotations/possible-duplicates`),
  // Self-hosted client-side error tracking (admin dashboard tab).
  listClientErrors: (params = {}) => axios.get(`${API}/client-errors`, { params }),
  resolveClientError: (id) => axios.post(`${API}/client-errors/${id}/resolve`),
  unresolveClientError: (id) => axios.post(`${API}/client-errors/${id}/unresolve`),
  clearResolvedClientErrors: () => axios.delete(`${API}/client-errors/clear-resolved`),
  // Delivery tracking (old)
  markDelivered: (id) => axios.post(`${API}/quotations/${id}/mark-delivered`),
  unmarkDelivered: (id) => axios.post(`${API}/quotations/${id}/unmark-delivered`),
  // Delivery Management (new)
  getDeliveryQuotations: (params) => axios.get(`${API}/delivery/quotations`, { params }),
  getDeliveryDetails: (id) => axios.get(`${API}/delivery/quotations/${id}`),
  deliverItems: (id, data) => axios.post(`${API}/delivery/quotations/${id}/deliver`, data),
  revertDelivery: (id, data) => axios.post(`${API}/delivery/quotations/${id}/revert`, data),
  getDeliveryUsers: () => axios.get(`${API}/delivery/users`),
  // Purchase Orders
  createPurchaseOrder: (data) => axios.post(`${API}/purchase-orders`, data),
  getPurchaseOrders: (params) => axios.get(`${API}/purchase-orders`, { params }),
  getPurchaseOrder: (id) => axios.get(`${API}/purchase-orders/${id}`),
  updatePurchaseOrder: (id, data) => axios.put(`${API}/purchase-orders/${id}`, data),
  updatePOStatus: (id, status, note) => axios.put(`${API}/purchase-orders/${id}/status`, null, { params: { status, ...(note ? { note } : {}) } }),
  deletePurchaseOrder: (id) => axios.delete(`${API}/purchase-orders/${id}`),
  downloadPOPdf: (id) => axios.get(`${API}/purchase-orders/${id}/pdf`, { responseType: 'blob' }),
  downloadTrainingManual: () => axios.get(`${API}/admin/training-manual`, { responseType: 'blob' }),
  downloadRolesGuide: () => axios.get(`${API}/admin/roles-guide`, { responseType: 'blob' }),
  downloadPendingReport: () => axios.get(`${API}/admin/pending-report`, { responseType: 'blob' }),
  downloadReportPdf: (params) => axios.get(`${API}/admin/report-pdf`, { params, responseType: 'blob' }),
  downloadProductUploadManual: () => axios.get(`${API}/admin/product-upload-manual`, { responseType: 'blob' }),
};

// ─── UTILS ───
// Format an INR amount with the ₹ prefix and Indian thousand grouping.
// Preserves decimals up to 2 places when present but never shows ".00"
// (so whole rupees look clean: ₹5,600 not ₹5,600.00). Critical for
// fractional per-piece pricing like ₹0.83/piece for fasteners/screws.
export const formatINR = (num) => {
  if (num === undefined || num === null) return '₹ 0';
  const n = Number(num);
  if (isNaN(n)) return '₹ 0';
  // Round to 2 decimals to avoid floating-point noise like 0.8300000004
  const rounded = Math.round(n * 100) / 100;
  // Use fractional digits only if needed (whole numbers stay clean).
  const opts = Number.isInteger(rounded)
    ? { maximumFractionDigits: 0 }
    : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
  return '₹ ' + rounded.toLocaleString('en-IN', opts);
};

// All timestamps in the database are stored UTC (Python `datetime.now(timezone.utc)`).
// HEDA staff are all in India, so render every timestamp explicitly in IST
// (Asia/Kolkata) — never trust the browser's local zone (which differs when
// staff travel abroad or use a VPN). These helpers are the single rendering
// path; do NOT call `.toLocaleString()` directly on dates anywhere else.
const _IST_OPTS_DATE = { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' };
const _IST_OPTS_DT   = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' };
const _IST_OPTS_DT_FULL = { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' };

export const formatISTDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('en-IN', _IST_OPTS_DATE); }
  catch { return ''; }
};
export const formatIST = (iso, { withSeconds = false } = {}) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-IN', withSeconds ? _IST_OPTS_DT_FULL : _IST_OPTS_DT);
  } catch { return ''; }
};
/** dd-mm-yyyy in IST — used in download filenames. */
export const formatISTDdMmYyyyForFilename = (iso) => {
  if (!iso) iso = new Date().toISOString();
  try {
    // en-GB gives dd/mm/yyyy; force IST then swap to dashes.
    return new Date(iso)
      .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' })
      .replace(/\//g, '-');
  } catch { return ''; }
};

// Categories that price PER SQUARE FOOT (rate × sqft × qty), as opposed
// to per-piece / per-sheet / per-set categories like Laminates, Hardware,
// Adhesives, etc. Used by Quick Add flows to multiply per-sqft rates by
// the sheet's square-foot area before storing the line amount.
// (ACP added 2026-06-11 — Aluminium Composite Panels are sold per sqft.)
export const SQFT_CATEGORIES = new Set([
  'Plywood', 'Veneers', 'Doors', 'MDF', 'WPC/PVC', 'Blockboard', 'ACP',
]);

// Iter 115: Cascading discount is now available for EVERY product
// category — Hardware, Adhesives, Laminates, Highlighters, ACP, Others
// were previously gated out, but the staff asked for the same compact
// "10+3+3" or "25+10" syntax on every line. We still check the per-item
// `discount_type === 'cascading'` flag, so non-cascading items continue
// to use the standard percent / amount path.
export const CASCADING_CATEGORIES = new Set([
  'Plywood', 'Blockboard', 'Laminates', 'Doors', 'WPC/PVC', 'Veneers',
  'Highlighters', 'MDF', 'ACP', 'Hardware', 'Adhesives', 'Others',
]);

export const isCascadingCategory = (cat) => CASCADING_CATEGORIES.has(cat);

// Convert a size string like "8x4" (feet), "84x36 in", "8x4 ft",
// "200x100 cm", or "2400x1200 mm" to square feet. When an explicit unit
// suffix is present (used by the Quick Add SizeInput), conversion is exact.
// Without a suffix, fall back to a legacy heuristic: Doors are inches; if
// either dim is >= 30 assume inches; otherwise feet.
export const sizeToSqft = (sizeStr, category) => {
  if (!sizeStr) return 0;
  const s = String(sizeStr).toLowerCase().trim();

  // ───────────────────────────────────────────────────────────────
  // MIXED-UNIT support (e.g. "4ft x 6in", "12in x 4ft", "200cm x 4ft").
  // Each side can carry its own unit; we convert both to feet and
  // multiply. This is the format the staff asked for on 11 Jun 2026 so
  // they can enter Wooden Flooring sizes naturally.
  //
  // We try this FIRST, then fall back to the legacy "trailing-unit OR
  // none" parser so existing data keeps working.
  // ───────────────────────────────────────────────────────────────
  const TO_FEET = { ft: 1, feet: 1, in: 1 / 12, inch: 1 / 12, inches: 1 / 12, cm: 1 / 30.48, mm: 1 / 304.8, m: 3.28084, meter: 3.28084, meters: 3.28084 };
  const sideRe = /(\d+(?:\.\d+)?)\s*(ft|feet|in|inch|inches|cm|mm|m|meter|meters)\b/g;
  const sides = [...s.matchAll(sideRe)];
  if (sides.length === 2) {
    const a = parseFloat(sides[0][1]) * TO_FEET[sides[0][2]];
    const b = parseFloat(sides[1][1]) * TO_FEET[sides[1][2]];
    if (!isNaN(a) && !isNaN(b) && a > 0 && b > 0) return a * b;
  }

  // ───────────────────────────────────────────────────────────────
  // LEGACY: trailing unit OR unit-less ("8x4", "84x36 in", "8x4 ft",
  // "200x100 cm"). Unit-less defaults to feet but applies the
  // dim >= 30 → inches heuristic and the Doors-category special case.
  // ───────────────────────────────────────────────────────────────
  let unit = null;
  const mUnit = s.match(/\b(ft|feet|in|inch|inches|cm|mm)\b\s*$/);
  if (mUnit) {
    const u = mUnit[1];
    unit = (u === 'feet') ? 'ft' : (u === 'inch' || u === 'inches') ? 'in' : u;
  }
  const cleaned = unit ? s.replace(mUnit[0], '').trim() : s;
  const parts = cleaned.split(/[x×]/).map(p => parseFloat(p.replace(/[^\d.]/g, '')));
  if (parts.length !== 2 || isNaN(parts[0]) || isNaN(parts[1])) return 0;
  const a = parts[0], b = parts[1];
  const raw = a * b;

  if (unit === 'ft') return raw;
  if (unit === 'in') return raw / 144;
  if (unit === 'cm') return raw / (30.48 * 30.48);
  if (unit === 'mm') return raw / (304.8 * 304.8);
  if (category === 'Doors' || a >= 30 || b >= 30) {
    return raw / 144;
  }
  return raw;
};

export const CATEGORY_ICONS = {
  'Plywood': 'Layers',
  'Laminates': 'Palette',
  'Highlighters': 'Sparkles',
  'Veneers': 'TreePine',
  'Wooden Flooring': 'LayoutGrid',
  'Hardware': 'Wrench',
  'ACP': 'PanelTop',
  'Adhesives': 'Pipette',
  'Doors': 'DoorOpen',
  'WPC/PVC': 'SquareStack',
  'MDF': 'Box',
};
